import React, { useState, useEffect } from 'react';
import { getChangeRequestParticipants } from '@/services/change-requests';
import { Trans } from '@lingui/react/macro';
import theme from './ChangeRequestDetails.module.scss';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { AlertBanner, Button } from '@ygtechnologies/rubus-ui';
import { ButtonBar, InternalLinkButton } from '@/components/Buttons';
import { faUserPlus, faUser, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import {
    ChangeRequest,
    BatchedChanges,
    UpdateFields,
    ProjectTag,
    BuyerOptionGroupOverride,
    ChangeRequestParticipantInfo,
    SellerChangeRequestStatus,
} from '@/types';
import { useAsyncState, usePrevious } from '@/utils/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChangeRequestParticipantsDialog from '../ChangeRequestParticipantsDialog';
import { ProjectPermissionToggle } from '@/components/permissions';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import { Tooltip } from '@/components/Tooltip';
import { UserInfoRow } from '@/components/UserInfoRow';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Tabs } from '@ygtechnologies/rubus-ui';
import ChangeRequestDetailsRequestTab from './ChangeRequestDetailsRequestTab';
import { isChangeRequestLocked } from '.';
import ChangeRequestDetailsOfferTab from './ChangeRequestDetailsOfferTab';
import ChangeRequestDetailsCommentTab from './ChangeRequestDetailsCommentTab';
import ChangeRequestDetailsOverridesTab from './ChangeRequestDetailsOverridesTab';
import TooltipLoading from '@/components/Tooltip/TooltipLoading';
import ChangeRequestDetailsHistoryLog from './ChangeRequestDetailsHistoryLog';
import Banner from '@/components/Banner';
import { getExternalAgreement } from '@/services/external-agreements';
import { uniqBy } from 'lodash-es';

interface ChangeRequestDetailsProps {
    changeRequest: ChangeRequest;
    onChange(changes: BatchedChanges<ChangeRequest>): void;
    onLoad?: () => void;
    // reloadChangeRequests?(): void;
}

const ChangeRequestDetails: React.FC<ChangeRequestDetailsProps> = ({ changeRequest, onChange, onLoad }) => {
    const [selectedTabId, setSelectedTabId] = useState('request');
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);

    const [showChangeParticipantsDialog, setShowChangeParticipantsDialog] = useState(false);
    const [participants, setParticipants, reloadParticipants] = useAsyncState(
        () => getChangeRequestParticipants(changeRequest.id),
        [changeRequest.id],
        null
    );
    const [externalAgreement] = useAsyncState(
        () =>
            changeRequest.externalAgreementId
                ? getExternalAgreement(changeRequest.externalAgreementId!)
                : Promise.resolve(null),
        [changeRequest.externalAgreementId],
        null
    );

    var previousParticipants = usePrevious(participants);
    useEffect(() => {
        if (!previousParticipants && participants && onLoad) {
            onLoad();
        }

        const isLocked = isChangeRequestLocked(changeRequest);
        if (!isLocked && participants && previousParticipants && participants !== previousParticipants) {
            let status = SellerChangeRequestStatus.AwaitingOffers;
            if (participants.length === 0) {
                status = SellerChangeRequestStatus.Unassigned;
            } else if (participants.every((p) => p.isReadyToSend)) {
                status = SellerChangeRequestStatus.ReadyToSend;
            }

            onChange(
                BatchedChanges.single(changeRequest.id, {
                    status,
                })
            );
        }
    }, [participants, previousParticipants, changeRequest, onLoad, onChange]);

    const handleParticipantChange = (changes: BatchedChanges<ChangeRequestParticipantInfo>) => {
        const updatedParticipants = changes.updateArray(participants!);
        setParticipants(updatedParticipants);

        const updatedTags = uniqBy(
            updatedParticipants.flatMap((p) => p.offerLines?.flatMap((ol) => ol.tags)).filter((x) => x),
            'id'
        ) as ProjectTag[];
        triggerChange({ tags: updatedTags });
    };

    const privateThread = useSelector((state: IState) =>
        state.seller.projectOverview.comments?.find((ct) => changeRequest.privateCommentThreadId === ct.threadId)
    );
    const publicThread = useSelector((state: IState) =>
        state.seller.projectOverview.comments?.find((ct) => changeRequest.publicCommentThreadId === ct.threadId)
    );

    const triggerChange = (changes: UpdateFields<ChangeRequest>) => {
        triggerHandler(onChange, BatchedChanges.single(changeRequest.id, changes));
    };

    const handleOptionOverrideSave = (newOverrides: BuyerOptionGroupOverride[]) => {
        triggerChange({ optionGroupOverrides: newOverrides });
    };

    const isLocked = isChangeRequestLocked(changeRequest);

    const renderParticipantTooltipContent = () => {
        if (!participants) {
            return <TooltipLoading />;
        }

        if (participants?.length === 0) {
            return <Trans>No participants</Trans>;
        }

        return participants!.map((p) => (
            <UserInfoRow key={p.id} displayName={p.displayName} userId={p.userId} profileName={p.profileName} />
        ));
    };

    return (
        <div className={theme.container}>
            <ButtonBar className={theme.buttonBar}>
                <Tooltip dynamicWidth content={renderParticipantTooltipContent()}>
                    <span className={theme.userCount}>
                        <FontAwesomeIcon icon={faUser} />
                        &nbsp;
                        {participants ? participants.length : '?'}
                    </span>
                </Tooltip>

                <ProjectPermissionToggle condition="canAssignChangeRequests">
                    <Button
                        icon={faUserPlus}
                        variant={participants && participants.length === 0 ? 'secondary' : 'tertiary'}
                        onClick={() => setShowChangeParticipantsDialog(true)}
                        disabled={isLocked}>
                        <Trans>Add participants</Trans>
                    </Button>
                </ProjectPermissionToggle>
            </ButtonBar>

            {changeRequest.externalAgreementId && (
                <AlertBanner type="info">
                    <Trans>
                        <strong>Please note,</strong> this change request originated from the following external agreement:
                        <InternalLinkButton
                            className={theme.externalAgreementLink}
                            icon={faExternalLink}
                            to={`/projects/${externalAgreement?.projectId}/residential-units/${externalAgreement?.residentialUnitId}/external-agreements/${externalAgreement?.id}`}>
                            {externalAgreement?.name}
                        </InternalLinkButton>
                    </Trans>
                </AlertBanner>
            )}

            <Tabs className={theme.tabs} activeId={selectedTabId} onChange={setSelectedTabId} size="sm" variant="underline">
                <Tabs.Tab id="request" label={<Trans>Request</Trans>} />
                <Tabs.Tab id="offer" label={<Trans>Offer</Trans>} />
                <Tabs.Tab
                    id="overrides"
                    label={
                        <>
                            <Trans>Overridden options</Trans>
                            {changeRequest.optionGroupOverrides.length > 0 && (
                                <>&nbsp;({changeRequest.optionGroupOverrides.length})</>
                            )}
                        </>
                    }
                />
                <Tabs.Tab
                    id="buyerChat"
                    label={
                        <>
                            <Trans>Buyer messages</Trans>
                            {publicThread?.unreadMessages ? ` (${publicThread.unreadMessages})` : null}
                        </>
                    }
                />
                <Tabs.Tab
                    id="internalChat"
                    label={
                        <>
                            <Trans>Internal messages</Trans>
                            {privateThread?.unreadMessages ? ` (${privateThread.unreadMessages})` : null}
                        </>
                    }
                />
                <Tabs.Tab id="historyLog" label={<Trans>History log</Trans>} />
            </Tabs>
            {selectedTabId === 'request' && (
                <ChangeRequestDetailsRequestTab changeRequest={changeRequest} onChange={onChange} />
            )}
            {selectedTabId === 'offer' &&
                (participants ? (
                    <ChangeRequestDetailsOfferTab
                        changeRequest={changeRequest}
                        participants={participants}
                        onParticipantChange={handleParticipantChange}
                        onChange={onChange}
                    />
                ) : (
                    <LoadingIndicator />
                ))}
            {selectedTabId === 'overrides' && (
                <ChangeRequestDetailsOverridesTab changeRequest={changeRequest} onChange={handleOptionOverrideSave} />
            )}
            {selectedTabId === 'buyerChat' && (
                <ChangeRequestDetailsCommentTab
                    buyerIncluded
                    key={changeRequest.publicCommentThreadId}
                    threadId={changeRequest.publicCommentThreadId}
                />
            )}
            {selectedTabId === 'internalChat' && (
                <ChangeRequestDetailsCommentTab
                    key={changeRequest.privateCommentThreadId}
                    threadId={changeRequest.privateCommentThreadId}
                />
            )}
            {selectedTabId === 'historyLog' && <ChangeRequestDetailsHistoryLog changeRequest={changeRequest} />}

            <ChangeRequestParticipantsDialog
                projectId={projectId}
                changeRequestId={changeRequest.id}
                show={showChangeParticipantsDialog}
                onClose={() => setShowChangeParticipantsDialog(false)}
                onSave={() => reloadParticipants()}
            />
        </div>
    );
};

export default ChangeRequestDetails;
